/**
 * chatShared.jsx — ของที่หน้าแชทเต็มจอ (/chat) กับกล่องแชทลอย (ChatDock) ใช้ร่วมกัน
 * แก้ที่เดียวแล้วเหมือนกันทั้งสองที่ — ไม่ต้องไล่แก้สองรอบ
 *
 * 16 ต.ค. 69 — รูปกลุ่ม (RoomAvatar) · ตอบกลับข้อความ (ReplyBar + กล่องอ้างอิงเหนือข้อความ)
 *              · แท็กชื่อคน (MentionTextarea: พิมพ์ @ แล้วเลือกชื่อ) — migration 125
 * 23 ต.ค. 69 — 2 ภาษา TH/EN: T(ไทย, อังกฤษ) อ่านภาษาปัจจุบันจาก i18n (ใช้ได้ทั้งในคอมโพเนนต์และฟังก์ชันธรรมดา)
 */
import React from "react";
import { createPortal } from "react-dom";
import { FileText, Download, Trash2, Users, CornerUpLeft, X, Bot, Volume2, Square, Smile } from "lucide-react";
import { canSpeak, speakMessage, stopSpeaking, currentSpeaking } from "./chatSpeak";
import { CHAT_URL } from "../../constants";
import socket from "../../socket";
import i18n from "../../i18n";

const isEn = () => Boolean(i18n.language?.startsWith("en"));
export const T = (th, en) => (isEn() ? en : th);
const locale = () => (isEn() ? "en-GB" : "th-TH");

export const initials = (name) => String(name || "?").trim().slice(0, 2).toUpperCase();

export const fileSize = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export const clock = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
};

export const dayLabel = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return T("วันนี้", "Today");
  const y = new Date(today);
  y.setDate(y.getDate() - 1);
  if (same(d, y)) return T("เมื่อวาน", "Yesterday");
  return d.toLocaleDateString(locale(), { day: "numeric", month: "short", year: "2-digit" });
};

// สีพื้นของวงกลมชื่อย่อ — คงที่ต่อคน จะได้จำกันได้จากสี
const AVATAR_TONES = [
  "bg-indigo-100 text-indigo-700", "bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700", "bg-sky-100 text-sky-700", "bg-violet-100 text-violet-700",
];
export const toneOf = (id) => AVATAR_TONES[Math.abs(Number(id) || 0) % AVATAR_TONES.length];

// ── ใครออนไลน์ (จุดเขียวแบบ Facebook · 23 ต.ค. 69) ─────────────────────────────
//   ตัวเก็บกลางตัวเดียวทั้งเว็บ: โหลด /api/chat/online ครั้งแรก + ทุก 60 วิ + ตอน socket ต่อใหม่
//   แล้วรับ "presence:changed" จากหลังบ้านทันทีที่มีคนเปิดแท็บแรก/ปิดแท็บสุดท้าย (server.js)
//   ออนไลน์ = เปิดหน้าเว็บอยู่ (หน้าไหนก็ได้) ไม่ใช่เฉพาะหน้าแชท
const online = { ids: new Set(), listeners: new Set(), started: false };
const emitOnline = () => online.listeners.forEach((f) => f(online.ids));
const startOnline = () => {
  if (online.started) return;
  online.started = true;
  const load = () => fetch(`${CHAT_URL}/online`, { credentials: "include" })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { if (d?.ids) { online.ids = new Set(d.ids.map(Number)); emitOnline(); } })
    .catch(() => {});
  load();
  setInterval(load, 60000);
  socket.on("connect", load);
  socket.on("presence:changed", ({ userId, online: on } = {}) => {
    const s = new Set(online.ids);
    if (on) s.add(Number(userId)); else s.delete(Number(userId));
    online.ids = s;
    emitOnline();
  });
};
export function useOnlineIds() {
  const [ids, setIds] = React.useState(online.ids);
  React.useEffect(() => {
    startOnline();
    online.listeners.add(setIds);
    setIds(online.ids);
    return () => { online.listeners.delete(setIds); };
  }, []);
  return ids;
}
// จุดเขียวมุมขวาล่างของรูป
export const OnlineDot = ({ show }) => (show ? (
  <span className="absolute bottom-0 right-0 h-[30%] w-[30%] min-h-[0.6rem] min-w-[0.6rem] rounded-full bg-emerald-500 ring-2 ring-white" title={T("ออนไลน์อยู่", "Online")} />
) : null);

// รูปคน: รูปในทะเบียนพนักงาน (photo) → ไม่มีรูปแต่เป็น AI = ไอคอนหุ่นยนต์ → ไม่งั้นวงกลมชื่อย่อ
//   ออนไลน์อยู่ = จุดเขียวมุมขวาล่าง (useOnlineIds)
export function Avatar({ id, name, size = "w-9 h-9", photo, ai }) {
  const [broken, setBroken] = React.useState(false);
  const isOnline = useOnlineIds().has(Number(id));
  React.useEffect(() => setBroken(false), [photo]);
  let inner;
  if (photo && !broken) {
    inner = <img src={`/api/asset/${photo}`} alt={name || ""} onError={() => setBroken(true)} className={`${size} rounded-full object-cover shrink-0 bg-slate-100 ring-1 ring-slate-200`} />;
  } else if (ai) {
    inner = (
      <div className={`${size} rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0`} title={T("ผู้ช่วย AI", "AI assistant")}>
        <Bot className="w-[55%] h-[55%]" />
      </div>
    );
  } else {
    inner = (
      <div className={`${size} ${toneOf(id)} rounded-full flex items-center justify-center text-[11px] font-black shrink-0`}>
        {initials(name)}
      </div>
    );
  }
  if (!isOnline) return inner;
  return <span className={`relative inline-flex shrink-0 ${size}`}>{inner}<OnlineDot show /></span>;
}

// รูปห้อง — กลุ่มที่ตั้งรูปไว้ใช้รูปนั้น · ไม่มีรูป = วงกลมไอคอนคน · ตัวต่อตัว = วงกลมชื่อย่อ
export function RoomAvatar({ room, size = "w-9 h-9" }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [room?.avatar_url]);
  if (!room) return <div className={`${size} rounded-full bg-slate-200 shrink-0`} />;
  if (room.kind === "DM") return <Avatar id={room.peer?.id} name={room.name} size={size} photo={room.peer?.photo} ai={room.peer?.is_ai} />;
  if (room.avatar_url && !broken) {
    return <img src={room.avatar_url} alt="" onError={() => setBroken(true)} className={`${size} rounded-full object-cover shrink-0 bg-slate-100 ring-1 ring-slate-200`} />;
  }
  return (
    <div className={`${size} rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0`}>
      <Users className="w-[45%] h-[45%]" />
    </div>
  );
}

// ข้อความที่มี @ชื่อ — ทำชื่อที่ถูกแท็กให้เด่น (ใช้ชื่อสมาชิกห้อง ยาวก่อนสั้น กันชื่อซ้อนกัน)
// ลิงก์ในข้อความกดเปิดได้ (29 ต.ค. 69 ผู้ใช้: "ส่ง LINK URL แล้วเปิดไม่ได้") — http(s):// และ www.
//   ตัดเครื่องหมายวรรคตอนท้ายลิงก์ออก (เช่น "ดูที่ https://x.com/a." จุดท้ายไม่ใช่ส่วนของลิงก์)
const URL_RE = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;
function Linkified({ text, mine }) {
  return String(text).split(URL_RE).map((part, i) => {
    if (i % 2 === 0) return <React.Fragment key={i}>{part}</React.Fragment>;
    const m = part.match(/^(.*?)([.,;:!?)\]]*)$/);
    const url = m ? m[1] : part;
    const tail = m ? m[2] : "";
    return (
      <React.Fragment key={i}>
        <a href={/^https?:/i.test(url) ? url : `https://${url}`} target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`break-all font-semibold underline ${mine ? "text-white" : "text-blue-700"}`}>{url}</a>{tail}
      </React.Fragment>
    );
  });
}

function BodyText({ text, names, mine }) {
  if (!text) return text;
  if (!names?.length) return <Linkified text={text} mine={mine} />;
  const esc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(@(?:${[...names].sort((a, b) => b.length - a.length).map(esc).join("|")}))`, "g");
  return String(text).split(re).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className={`font-bold rounded px-0.5 ${mine ? "bg-white/20 text-white" : "bg-indigo-50 text-indigo-700"}`}>{part}</span>
    ) : (
      <Linkified key={i} text={part} mine={mine} />
    ),
  );
}

// เลื่อนลงข้อความล่าสุดตอนเปิดห้อง/มีข้อความใหม่ — รอรูปโหลดด้วย (29 ต.ค. 69 ผู้ใช้: "กดเข้าไปแล้วย้อนแชทไปเก่าเกิน ต้องเลื่อนลง")
//   เดิมเลื่อนครั้งเดียวก่อนรูปโหลด พอรูปโหลดเสร็จเนื้อหายาวขึ้น จุดที่เห็นเลยค้างอยู่ข้างบน
//   ตอนนี้: เลื่อนทันที + ซ้ำหลังรูปแต่ละรูปโหลด (ช่วง 4 วินาทีแรก หรือเมื่อยังอยู่ใกล้ล่างสุด) ไม่ดึงลงถ้าคนเลื่อนขึ้นไปอ่านเอง
export function useStickToBottom(listRef, bottomRef, deps) {
  React.useEffect(() => {
    const go = () => bottomRef.current?.scrollIntoView({ block: "end" });
    const start = Date.now();
    go();
    const timers = [120, 500, 1200].map((ms) => setTimeout(go, ms));
    const list = listRef.current;
    const onLoad = (e) => {
      if (!list || !["IMG", "VIDEO"].includes(e.target?.tagName)) return;
      const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 300;
      if (Date.now() - start < 4000 || nearBottom) go();
    };
    list?.addEventListener("load", onLoad, true);
    return () => { timers.forEach(clearTimeout); list?.removeEventListener("load", onLoad, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// ปุ่มอ่านออกเสียง (ไทยอ่านไทย อังกฤษอ่านอังกฤษ — chatSpeak.js) · กดอีกทีระหว่างอ่าน = หยุด
function SpeakButton({ id, text, always = false }) {
  const [on, setOn] = React.useState(() => currentSpeaking() === id);
  React.useEffect(() => {
    const h = (e) => setOn(e.detail === id);
    window.addEventListener("chat-speak", h);
    return () => window.removeEventListener("chat-speak", h);
  }, [id]);
  if (!canSpeak() || !text) return null;
  return (
    <button
      type="button"
      onClick={() => (on ? stopSpeaking() : speakMessage(id, text))}
      title={on ? T("หยุดอ่าน", "Stop reading") : T("อ่านออกเสียง", "Read aloud")}
      className={`mt-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[13px] font-semibold focus:opacity-100 ${
        on ? "text-rose-600 bg-rose-50" : `text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 ${always ? "" : "md:opacity-0 md:group-hover/msg:opacity-100"}`
      }`}
    >
      {on ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />} {on ? T("หยุด", "Stop") : T("ฟัง", "Listen")}
    </button>
  );
}

// เลื่อนไปข้อความต้นทางแล้วกะพริบให้เห็น (ถ้ายังไม่ได้โหลดมา ก็ไม่ทำอะไร)
export const jumpToMessage = (id) => {
  const el = document.getElementById(`chat-msg-${id}`);
  if (!el) return false;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  el.classList.add("ring-2", "ring-amber-400");
  setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 1600);
  return true;
};

// ข้อความหนึ่งก้อน — compact = ย่อให้พอดีกล่องลอย
//   onReply = กด "ตอบกลับ" · memberNames = ชื่อในห้อง (ไว้ทำ @ชื่อ ให้เด่น) · myId = ข้อความที่แท็กเรามีกรอบเหลือง
export function MessageRow({ m, mine, showHead, onDelete, onReply, memberNames, myId, compact = false, read = null }) {
  // ข้อความที่มีแต่อิโมจิ (ไม่เกิน 3 ตัว) = แสดงตัวใหญ่ไม่มีกรอบ แบบสติกเกอร์ (19 ต.ค. 69)
  const onlyEmoji = !m.deleted && !m.reply && !m.files?.length && isEmojiOnly(m.body);
  const [openFile, setOpenFile] = React.useState(null);
  const bubble = onlyEmoji ? "px-1 py-0 text-[40px] leading-tight" : compact ? "px-3 py-1.5 text-[13px]" : "px-3.5 py-2 text-[15px]";
  const tagsMe = !mine && myId && (m.mentions || []).map(Number).includes(Number(myId));
  return (
    <div className={`group/msg flex gap-2 ${showHead ? "mt-2.5" : "mt-0.5"} ${mine ? "flex-row-reverse" : ""}`}>
      {/* หน้าต่างแชตเล็กก็ต้องเห็นรูปคนส่ง (19 ต.ค. 69 · "โปรไฟล์ไม่ขึ้น") — ข้อความของตัวเองไม่ต้องมีรูป */}
      {!mine && <div className={`${compact ? "w-7" : "w-9"} shrink-0`}>{showHead && <Avatar id={m.user_id} name={m.user_name} photo={m.user_photo} ai={m.user_is_ai} size={compact ? "w-7 h-7" : undefined} />}</div>}
      <div className={`max-w-[min(42rem,78%)] ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {showHead && (
          <div className={`flex items-center gap-2 mb-0.5 ${mine ? "flex-row-reverse" : ""}`}>
            <span className="text-[11px] font-bold text-slate-600">{mine ? T("คุณ", "You") : m.user_name}</span>
            <span className="text-[10px] text-slate-400">{clock(m.created_at)}</span>
          </div>
        )}
        <div
          id={`chat-msg-${m.id}`}
          className={`group relative rounded-2xl whitespace-pre-wrap break-words transition-shadow ${bubble} ${
            m.deleted
              ? "bg-slate-50 text-slate-400 italic ring-1 ring-slate-200"
              : onlyEmoji
                ? "bg-transparent"
                : mine
                ? "bg-slate-900 text-white"
                : tagsMe
                  ? "bg-amber-50 text-slate-800 ring-2 ring-amber-300"
                  : "bg-white text-slate-800 ring-1 ring-slate-200"
          }`}
        >
          {!m.deleted && m.reply && (
            <button
              type="button"
              onClick={() => jumpToMessage(m.reply.id)}
              title={T("ไปที่ข้อความต้นทาง", "Go to the original message")}
              className={`mb-1.5 block w-full text-left rounded-lg border-l-4 px-2 py-1 text-[13px] not-italic ${
                mine ? "border-white/50 bg-white/10 text-white/85" : "border-indigo-400 bg-slate-50 text-slate-600"
              }`}
            >
              <span className="block font-bold truncate">{m.reply.user_name || T("ข้อความ", "Message")}</span>
              <span className="block truncate">{m.reply.text}</span>
            </button>
          )}
          {m.deleted ? T("ลบข้อความแล้ว", "Message deleted") : <BodyText text={m.body} names={memberNames} mine={mine} />}

          {!m.deleted && m.files?.length > 0 && (
            <div className={`${m.body ? "mt-2" : ""} space-y-1.5`}>
              {m.files.map((f) =>
                f.is_image ? (
                  <a
                    key={f.id}
                    href={`${f.url}?inline=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    onClick={(e) => { e.preventDefault(); setOpenFile(f); }}
                  >
                    <img
                      src={`${f.url}?inline=1`}
                      alt={f.orig_name}
                      className={`${compact ? "max-h-40" : "max-h-64"} rounded-xl border border-slate-200 bg-white`}
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <a
                    key={f.id}
                    href={f.url}
                    onClick={(e) => { if (isStandaloneApp()) { e.preventDefault(); setOpenFile(f); } }}
                    className={`flex items-center gap-2 rounded-xl px-2.5 py-2 text-xs ${
                      mine ? "bg-white/10 hover:bg-white/20" : "bg-slate-50 hover:bg-slate-100 ring-1 ring-slate-200"
                    }`}
                  >
                    <FileText className="w-4 h-4 shrink-0" />
                    <span className="truncate max-w-[14rem]">{f.orig_name}</span>
                    <span className={mine ? "text-white/60" : "text-slate-400"}>{fileSize(f.size_bytes)}</span>
                    <Download className="w-3.5 h-3.5 ml-auto shrink-0" />
                  </a>
                ),
              )}
            </div>
          )}
          {openFile && <ChatFileViewer file={openFile} onClose={() => setOpenFile(null)} />}

          {mine && !m.deleted && onDelete && (
            <button
              type="button"
              onClick={() => onDelete(m)}
              title={T("ลบข้อความนี้", "Delete this message")}
              className="absolute -left-7 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* ตอบกลับ + ฟัง — จอคอมขึ้นตอนชี้ (ข้อความของ AI เห็นปุ่มฟังตลอด) · มือถือเห็นตลอด */}
        {!m.deleted && (onReply || m.body) && (
          <div className={`flex items-center gap-0.5 ${mine ? "flex-row-reverse" : ""}`}>
            {mine && read && (
              <span className={`mt-0.5 px-1 text-[11px] font-bold ${read.n ? "text-emerald-700" : "text-slate-400"}`}
                title={read.n ? T("คนในห้องเปิดอ่านข้อความนี้แล้ว", "Read by people in this room") : T("ยังไม่มีใครเปิดอ่าน", "Nobody has read it yet")}>
                {read.n ? (read.dm ? T("อ่านแล้ว", "Read") : `${T("อ่านแล้ว", "Read by")} ${read.n}`) : T("ยังไม่อ่าน", "Unread")}
              </span>
            )}
            {onReply && (
              <button
                type="button"
                onClick={() => onReply(m)}
                className="mt-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[13px] font-semibold text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 md:opacity-0 md:group-hover/msg:opacity-100 focus:opacity-100"
              >
                <CornerUpLeft className="w-3.5 h-3.5" /> {T("ตอบกลับ", "Reply")}
              </button>
            )}
            <SpeakButton id={m.id} text={m.body} always={Boolean(m.user_is_ai)} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── อิโมจิ + คำตอบด่วน (19 ต.ค. 69 · ผู้ใช้: "ใส่สติกเกอร์อารมณ์หรืออิโมจิ ให้เลือก โอเค รับทราบ ขอบคุณ สวัสดี") ──
//   คำตอบด่วน = กดแล้วส่งเลย · อิโมจิ = ใส่ต่อท้ายช่องพิมพ์ (ส่งเองตอนพร้อม) · ส่งอิโมจิอย่างเดียวจะขึ้นตัวใหญ่
// ── เปิดรูป / ไฟล์ในแชทโดยไม่ออกจากแอป ─────────────────────────────────
// เว็บที่ "เพิ่มไปยังหน้าจอโฮม" บน iPad/iPhone ไม่มีปุ่มย้อนกลับ — เดิมกดรูป/ไฟล์แล้วหน้าเปลี่ยนไปที่ไฟล์ ออกไม่ได้ (2 พ.ย. 69)
// รูป = เปิดทับในหน้าเดิมมีปุ่มปิด · ไฟล์ (เฉพาะโหมดแอป) = แผ่นเล็กให้กดบันทึก/เปิดในแอปอื่นผ่านเมนูแชร์ของเครื่อง
export const isStandaloneApp = () =>
  (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches) || window.navigator?.standalone === true;

function ChatFileViewer({ file, onClose }) {
  const [shareFile, setShareFile] = React.useState(null);
  const [err, setErr] = React.useState("");
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // โหลดไฟล์ไว้ก่อน — ปุ่มแชร์ของ iOS ต้องเรียกทันทีที่กด (รอโหลดหลังกดไม่ได้)
  React.useEffect(() => {
    if (file.is_image && !isStandaloneApp()) return undefined;
    let alive = true;
    fetch(file.url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => alive && setShareFile(new File([b], file.orig_name || "file", { type: b.type || "application/octet-stream" })))
      .catch(() => alive && setErr(T("โหลดไฟล์ไม่สำเร็จ", "Could not load the file")));
    return () => { alive = false; };
  }, [file]);
  const canShare = !!shareFile && !!navigator.canShare?.({ files: [shareFile] });
  const share = () => navigator.share({ files: [shareFile], title: file.orig_name }).catch(() => {});
  const btn = "h-11 rounded-md px-4 text-[15px] font-bold";

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex flex-col bg-black/85" role="dialog" aria-modal="true" aria-label={file.orig_name} onClick={onClose}>
      <div className="flex items-center gap-2 bg-black/60 px-3 py-2" style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }} onClick={(e) => e.stopPropagation()}>
        <span className="min-w-0 flex-1 truncate text-[15px] text-white">{file.orig_name}</span>
        {canShare && (
          <button type="button" onClick={share} className={`${btn} border border-white/40 bg-white/10 text-white`}>
            {T("บันทึก / เปิดในแอปอื่น", "Save / open in…")}
          </button>
        )}
        <button type="button" onClick={onClose} className={`${btn} flex items-center gap-1 bg-white text-slate-900`}>
          <X className="h-5 w-5" /> {T("ปิด", "Close")}
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-3">
        {file.is_image ? (
          <img src={`${file.url}?inline=1`} alt={file.orig_name} className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
        ) : (
          <div className="w-full max-w-sm rounded-md bg-white p-4 text-center text-[15px] text-slate-800" onClick={(e) => e.stopPropagation()}>
            <FileText className="mx-auto mb-2 h-10 w-10 text-slate-500" />
            <div className="break-all font-bold">{file.orig_name}</div>
            <div className="mb-3 text-slate-500">{fileSize(file.size_bytes)}</div>
            {err ? <div className="font-bold text-rose-700">{err}</div>
              : !shareFile ? <div className="text-slate-600">{T("กำลังโหลดไฟล์…", "Loading…")}</div>
              : canShare ? (
                <button type="button" onClick={share} className={`${btn} w-full bg-emerald-600 text-white`}>
                  {T("บันทึก / เปิดในแอปอื่น", "Save / open in…")}
                </button>
              ) : (
                <a href={URL.createObjectURL(shareFile)} download={file.orig_name} className={`${btn} flex w-full items-center justify-center bg-emerald-600 text-white`}>
                  {T("บันทึกไฟล์", "Save file")}
                </a>
              )}
          </div>
        )}
      </div>
      {file.is_image && (
        <div className="pb-3 text-center text-[13px] text-white/70" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          {T("แตะที่ว่างหรือกด ปิด เพื่อกลับ · กดค้างที่รูปเพื่อบันทึก", "Tap outside or Close to go back · long-press to save")}
        </div>
      )}
    </div>,
    document.body,
  );
}

export function isEmojiOnly(text) {
  const t = String(text || "").trim();
  if (!t || t.length > 16) return false;
  if (!/^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\uFE0F|\u200D|\s)+$/u.test(t)) return false;
  return (t.match(/\p{Extended_Pictographic}/gu) || []).length <= 3;
}
const QUICK_REPLIES = ["รับทราบ 👍", "โอเค 👌", "ขอบคุณ 🙏", "สวัสดี 😊", "ได้เลย 💪", "เรียบร้อยแล้ว ✅", "เดี๋ยวดูให้ 🔍", "รอสักครู่ ⏳", "ขอโทษ 🙏", "เยี่ยมมาก 👏"];
const QUICK_REPLIES_EN = ["Noted 👍", "OK 👌", "Thank you 🙏", "Hello 😊", "Sure 💪", "Done ✅", "Let me check 🔍", "One moment ⏳", "Sorry 🙏", "Great job 👏"];
const EMOJIS = ["👍", "🙏", "😊", "😂", "🤣", "😅", "😍", "🥰", "😎", "🤔", "😮", "😢", "😭", "😡", "😴", "🤝",
  "👌", "👏", "💪", "✌️", "❤️", "🔥", "🎉", "✅", "❌", "⚠️", "⏳", "📦", "🔧", "💡", "📌", "☕"];
export function EmojiPicker({ onInsert, onQuick, disabled = false, compact = false }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div ref={ref} className="relative shrink-0">
      <button type="button" onClick={() => setOpen((v) => !v)} disabled={disabled} aria-expanded={open}
        title={T("อิโมจิ / คำตอบด่วน", "Emoji / quick replies")} className={`${compact ? "p-2" : "p-2.5"} rounded-xl ${open ? "bg-amber-100 text-amber-700" : "text-slate-500 hover:bg-slate-100"}`}>
        <Smile className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-50 mb-2 w-[17.5rem] rounded-md border border-slate-300 bg-white p-2 shadow-xl">
          <div className="mb-1 text-[13px] font-bold text-slate-600">{T("ตอบด่วน (กดแล้วส่งเลย)", "Quick reply (sends right away)")}</div>
          <div className="mb-2 flex flex-wrap gap-1">
            {(isEn() ? QUICK_REPLIES_EN : QUICK_REPLIES).map((q) => (
              <button key={q} type="button" onClick={() => { onQuick(q); setOpen(false); }}
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[13px] font-bold text-slate-800 hover:border-emerald-500 hover:bg-emerald-50">{q}</button>
            ))}
          </div>
          <div className="mb-1 text-[13px] font-bold text-slate-600">{T("อิโมจิ (ใส่ในช่องพิมพ์)", "Emoji (adds to the message box)")}</div>
          <div className="grid grid-cols-8 gap-0.5">
            {EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => onInsert(e)} className="flex h-8 items-center justify-center rounded text-[20px] hover:bg-slate-100" aria-label={`${T("ใส่", "Insert")} ${e}`}>{e}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// รายการข้อความที่คั่นวันให้เรียบร้อย ใช้เหมือนกันทั้งสองที่
// อ่านแล้ว (19 ต.ค. 69) — นับจาก last_read_id ของสมาชิกคนอื่น · ห้องคุย 2 คน = "อ่านแล้ว" · กลุ่ม = "อ่านแล้ว N"
export function MessageList({ messages, myId, onDelete, onReply, memberNames, compact, members = [] }) {
  const others = members.filter((x) => Number(x.id) !== Number(myId) && !x.is_ai);
  const readCount = (msgId) => others.filter((x) => Number(x.last_read_id) >= Number(msgId)).length;
  return messages.map((m, i) => {
    const prev = messages[i - 1];
    const newDay = !prev || dayLabel(prev.created_at) !== dayLabel(m.created_at);
    const showHead = newDay || !prev || prev.user_id !== m.user_id;
    return (
      <React.Fragment key={m.id}>
        {newDay && (
          <div className="my-3 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-[10px] font-bold text-slate-400">{dayLabel(m.created_at)}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>
        )}
        <MessageRow
          m={m} mine={Number(m.user_id) === myId} showHead={showHead} onDelete={onDelete} onReply={onReply}
          memberNames={memberNames} myId={myId} compact={compact}
          read={Number(m.user_id) === myId && others.length ? { n: readCount(m.id), dm: others.length === 1 } : null}
        />
      </React.Fragment>
    );
  });
}

// ── ข้อความของห้องหนึ่ง: โหลด · รับของใหม่ · ส่ง · ลบ ─────────────────────
export function fetchMessages(roomId, opts = {}) {
  const params = new URLSearchParams({ limit: String(opts.limit || 50) });
  if (opts.before) params.set("before", opts.before);
  if (opts.after) params.set("after", opts.after);
  return fetch(`${CHAT_URL}/rooms/${roomId}/messages?${params}`, { credentials: "include" }).then(async (r) => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d?.message || T("โหลดข้อความไม่สำเร็จ", "Could not load messages"));
    return d;
  });
}

export function postMessage(roomId, { body, files = [], replyToId, mentionIds = [] }) {
  const fd = new FormData();
  if (body) fd.append("body", body);
  if (replyToId) fd.append("reply_to_id", String(replyToId));
  if (mentionIds.length) fd.append("mention_ids", mentionIds.join(","));
  files.forEach((f) => fd.append("files", f));
  return fetch(`${CHAT_URL}/rooms/${roomId}/messages`, { method: "POST", credentials: "include", body: fd }).then(
    async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || T("ส่งไม่สำเร็จ", "Could not send"));
      return d.message;
    },
  );
}

/**
 * useRoomMessages — ข้อความของห้องที่เปิดอยู่
 * รับของใหม่ทาง Socket.IO · socket ขาดเมื่อไหร่ถอยไปดึงเองทุก 10 วินาที
 */
export function useRoomMessages(roomId, { onError } = {}) {
  const [messages, setMessages] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const latestRef = React.useRef(0);

  React.useEffect(() => {
    latestRef.current = messages.length ? messages[messages.length - 1].id : 0;
  }, [messages]);

  React.useEffect(() => {
    if (!roomId) { setMessages([]); return undefined; }
    let alive = true;
    setLoading(true);
    fetchMessages(roomId)
      .then((d) => {
        if (!alive) return;
        setMessages(d.messages || []);
        setHasMore(Boolean(d.has_more));
      })
      .catch((e) => alive && onError?.(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  React.useEffect(() => {
    const onMessage = ({ room_id: rid, message }) => {
      if (Number(rid) !== Number(roomId)) return;
      setMessages((ms) => (ms.some((m) => m.id === message.id) ? ms : [...ms, message]));
    };
    const onDeleted = ({ room_id: rid, message_id: mid }) => {
      if (Number(rid) !== Number(roomId)) return;
      setMessages((ms) => ms.map((m) => (m.id === mid ? { ...m, deleted: true, body: null, files: [] } : m)));
    };
    socket.on("chat:message", onMessage);
    socket.on("chat:deleted", onDeleted);
    return () => {
      socket.off("chat:message", onMessage);
      socket.off("chat:deleted", onDeleted);
    };
  }, [roomId]);

  React.useEffect(() => {
    if (!roomId) return undefined;
    const timer = setInterval(async () => {
      if (socket.connected) return;
      try {
        const d = await fetchMessages(roomId, { after: latestRef.current });
        if (d.messages?.length) {
          setMessages((ms) => [...ms, ...d.messages.filter((n) => !ms.some((m) => m.id === n.id))]);
        }
      } catch {
        // เงียบไว้ — รอบหน้าลองใหม่
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [roomId]);

  const loadOlder = React.useCallback(async () => {
    if (!messages.length) return;
    try {
      const d = await fetchMessages(roomId, { before: messages[0].id });
      setMessages((ms) => [...(d.messages || []), ...ms]);
      setHasMore(Boolean(d.has_more));
    } catch (e) {
      onError?.(e.message);
    }
  }, [messages, roomId, onError]);

  const appendMine = React.useCallback((message) => {
    setMessages((ms) => (ms.some((m) => m.id === message.id) ? ms : [...ms, message]));
  }, []);

  const markDeleted = React.useCallback((id) => {
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, deleted: true, body: null, files: [] } : m)));
  }, []);

  return { messages, loading, hasMore, loadOlder, appendMine, markDeleted, lastId: messages.length ? messages[messages.length - 1].id : 0 };
}

/**
 * useFileDrop — แนบไฟล์ได้ 3 ทาง เหมือนแอปแชททั่วไป
 *   ปุ่มคลิปหนีบ (มีอยู่แล้ว) · ลากไฟล์มาวางในกล่องแชท · กด Ctrl+V วางรูปที่แคปจอ
 * รับได้ครั้งละไม่เกิน 10 ไฟล์ ไฟล์ละไม่เกิน 50 MB (ตรงกับหลังบ้าน) เกินแล้วบอกทันที ไม่ต้องรอส่ง
 */
export const MAX_CHAT_FILES = 10;
export const MAX_CHAT_MB = 50;

export function useFileDrop(setFiles, { onError } = {}) {
  const [dragging, setDragging] = React.useState(false);
  const depth = React.useRef(0);

  const add = React.useCallback((list) => {
    const incoming = Array.from(list || []).filter(Boolean);
    if (!incoming.length) return;
    const tooBig = incoming.filter((f) => f.size > MAX_CHAT_MB * 1024 * 1024);
    if (tooBig.length) onError?.(`${T(`ไฟล์ใหญ่เกิน ${MAX_CHAT_MB} MB`, `Files over ${MAX_CHAT_MB} MB`)}: ${tooBig.map((f) => f.name).join(", ")}`);
    const ok = incoming.filter((f) => f.size <= MAX_CHAT_MB * 1024 * 1024);
    setFiles((fs) => {
      const next = [...fs, ...ok];
      if (next.length > MAX_CHAT_FILES) onError?.(T(`แนบได้ครั้งละไม่เกิน ${MAX_CHAT_FILES} ไฟล์`, `You can attach up to ${MAX_CHAT_FILES} files at a time`));
      return next.slice(0, MAX_CHAT_FILES);
    });
  }, [setFiles, onError]);

  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const dropProps = {
    onDragEnter: (e) => { if (!hasFiles(e)) return; e.preventDefault(); depth.current += 1; setDragging(true); },
    onDragOver: (e) => { if (hasFiles(e)) e.preventDefault(); },
    onDragLeave: (e) => { if (!hasFiles(e)) return; depth.current = Math.max(0, depth.current - 1); if (!depth.current) setDragging(false); },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      add(e.dataTransfer.files);
    },
  };

  // วางรูปจากคลิปบอร์ด (แคปจอแล้ว Ctrl+V) — ตั้งชื่อให้ เพราะรูปจากคลิปบอร์ดชื่อ "image.png" หมด
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter((it) => it.kind === "file");
    if (!items.length) return;
    e.preventDefault();
    const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
    add(items.map((it, i) => {
      const f = it.getAsFile();
      if (!f) return null;
      const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
      return new File([f], f.name && f.name !== "image.png" ? f.name : `${T("แคปจอ", "screenshot")}-${stamp}${i ? `-${i}` : ""}.${ext}`, { type: f.type });
    }));
  };

  return { dragging, dropProps, onPaste, add };
}

// แผ่นทับตอนลากไฟล์มาวาง
export const DropOverlay = ({ show }) =>
  show ? (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-indigo-400 bg-indigo-50/85">
      <span className="text-sm font-bold text-indigo-700">{T("วางไฟล์ตรงนี้เพื่อแนบ", "Drop files here to attach")}</span>
    </div>
  ) : null;

// ── ตอบกลับข้อความ: แถบเหนือช่องพิมพ์ ───────────────────────────────────────
export const ReplyBar = ({ reply, onClear }) =>
  reply ? (
    <div className={`mb-1.5 flex items-start gap-2 rounded-lg border-l-4 border-indigo-400 bg-indigo-50 px-2.5 py-1.5 text-[13px]`}>
      <CornerUpLeft className="w-4 h-4 mt-0.5 text-indigo-600 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-bold text-indigo-800 truncate">{T("ตอบกลับ", "Replying to")} {reply.user_name}</div>
        <div className="text-slate-600 truncate">{reply.body || (reply.files?.length ? T(`ส่งไฟล์ ${reply.files.length} ไฟล์`, `${reply.files.length} files`) : "")}</div>
      </div>
      <button type="button" onClick={onClear} title={T("ยกเลิกการตอบกลับ", "Cancel reply")} className="p-1 rounded-md text-slate-500 hover:bg-white hover:text-rose-600">
        <X className="w-4 h-4" />
      </button>
    </div>
  ) : null;

// แท็กที่ยังอยู่ในข้อความตอนกดส่ง (ลบ @ชื่อ ออกไปแล้ว = ไม่แท็ก)
export const mentionIdsIn = (text, picked, members) =>
  [...picked].filter((id) => {
    const u = (members || []).find((x) => Number(x.id) === Number(id));
    return u?.name && String(text).includes(`@${u.name}`);
  });

/**
 * MentionTextarea — ช่องพิมพ์ที่พิมพ์ @ แล้วขึ้นรายชื่อคนในห้องให้เลือก (แบบ LINE)
 *   ↑ ↓ เลือก · Enter/Tab ใส่ชื่อ · Esc ปิด — ตอนรายชื่อเปิดอยู่ Enter ไม่ส่งข้อความ
 *   onPick(id) = เลือกใครแล้ว (หน้าจอเก็บไว้ส่งเป็น mention_ids)
 */
export function MentionTextarea({ value, onChange, onKeyDown, onPick, members, myId, ...rest }) {
  const ref = React.useRef(null);
  const [q, setQ] = React.useState(null); // null = ปิด · "" = เพิ่งพิมพ์ @
  const [at, setAt] = React.useState(0);
  const [sel, setSel] = React.useState(0);

  const list = React.useMemo(() => {
    if (q === null) return [];
    const needle = q.toLowerCase();
    return (members || [])
      .filter((u) => Number(u.id) !== Number(myId) && u.name)
      .filter((u) => !needle || u.name.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [q, members, myId]);

  const detect = (text, caret) => {
    const before = text.slice(0, caret);
    const m = before.match(/(^|\s)@([^\s@]{0,30})$/);
    if (m) { setQ(m[2]); setAt(caret - m[2].length - 1); setSel(0); } else setQ(null);
  };

  const choose = (u) => {
    const caret = ref.current?.selectionStart ?? value.length;
    const next = `${value.slice(0, at)}@${u.name} ${value.slice(caret)}`;
    onChange(next);
    onPick?.(Number(u.id));
    setQ(null);
    const pos = at + u.name.length + 2;
    requestAnimationFrame(() => { ref.current?.focus(); ref.current?.setSelectionRange(pos, pos); });
  };

  return (
    <div className="relative flex-1 min-w-0">
      {q !== null && list.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1.5 z-20 w-[min(18rem,100%)] max-h-60 overflow-y-auto rounded-lg bg-white shadow-xl ring-1 ring-slate-300">
          <div className="px-3 py-1.5 text-[13px] font-semibold text-slate-500 border-b border-slate-100">{T("แท็กคนในห้อง", "Tag someone in this room")}</div>
          {list.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); choose(u); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[15px] ${i === sel ? "bg-indigo-50 text-indigo-800" : "text-slate-800 hover:bg-slate-50"}`}
            >
              <Avatar id={u.id} name={u.name} size="w-7 h-7" photo={u.photo} ai={u.is_ai} />
              <span className="truncate">{u.name}</span>
            </button>
          ))}
        </div>
      )}
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }}
        onKeyDown={(e) => {
          if (q !== null && list.length) {
            if (e.key === "ArrowDown") { e.preventDefault(); setSel((x) => (x + 1) % list.length); return; }
            if (e.key === "ArrowUp") { e.preventDefault(); setSel((x) => (x - 1 + list.length) % list.length); return; }
            if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); choose(list[sel]); return; }
            if (e.key === "Escape") { e.preventDefault(); setQ(null); return; }
          }
          onKeyDown?.(e);
        }}
        onClick={(e) => detect(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(() => setQ(null), 150)}
        {...rest}
      />
    </div>
  );
}
